import { useEffect, useState } from 'react'
import { SAAS_ACCOUNT_PLATFORMS, getSaasAccountByDomain } from '../services/saas-account.service'

const OWNER_DOMAINS = [
  'testingtracking.yallafelsekka.com',
  'tracking.yfsdelivery.com',
  'localhost',
  '127.0.0.1'
]
const STORAGE_KEY = 'saasAccount'
const RELOAD_STORAGE_KEY = 'saasBrandingReloaded'
const pendingAccountRequests = {}

const fetchSaasAccountByDomain = (hostname) => {
  if (!pendingAccountRequests[hostname]) {
    pendingAccountRequests[hostname] = getSaasAccountByDomain(hostname, SAAS_ACCOUNT_PLATFORMS.TRACKING).finally(() => {
      delete pendingAccountRequests[hostname]
    })
  }

  return pendingAccountRequests[hostname]
}

const applyFavicon = (url) => {
  if (!url) return
  try {
    let link = document.querySelector("link[rel~='icon']")
    if (!link) {
      link = document.createElement('link')
      link.rel = 'icon'
      document.head.appendChild(link)
    }
    link.href = url
  } catch (_) {}
}

const getCachedAccount = (parsed) => parsed?.account || parsed

const isOwnerDomain = (hostname) => OWNER_DOMAINS.includes(hostname)

const isAccountForTrackingHost = (account, hostname, cachedHostname) => {
  if (!account) return false
  if (cachedHostname === hostname) return true
  if (account.subdomain_slug && hostname.startsWith(`tracking${account.subdomain_slug}.`)) return true

  if (account.customer_portal_custom_domain && hostname.startsWith('tracking.')) {
    const trackingBaseDomain = hostname.replace(/^tracking\./, '')
    return (
      account.customer_portal_custom_domain === trackingBaseDomain ||
      account.customer_portal_custom_domain.endsWith(`.${trackingBaseDomain}`)
    )
  }

  return false
}

const useSaasAccount = () => {
  const hostname = window.location.hostname
  const isOwner = isOwnerDomain(hostname)

  const [logoUrl, setLogoUrl] = useState(null)
  const [favIconUrl, setFavIconUrl] = useState(null)
  const [accountName, setAccountName] = useState(null)
  const [saasAccountId, setSaasAccountId] = useState(null)
  const [isBrandingLoading, setIsBrandingLoading] = useState(!isOwner)
  const [brandingResolved, setBrandingResolved] = useState(isOwner)
  const [brandingFailed, setBrandingFailed] = useState(false)

  const handleBrandingFailure = () => {
    if (!sessionStorage.getItem(RELOAD_STORAGE_KEY)) {
      sessionStorage.setItem(RELOAD_STORAGE_KEY, '1')
      window.location.reload()
      return
    }

    setLogoUrl(null)
    setFavIconUrl(null)
    setAccountName(null)
    setSaasAccountId(null)
    setBrandingFailed(true)
    setIsBrandingLoading(false)
    setBrandingResolved(true)
  }

  useEffect(() => {
    if (isOwner) {
      sessionStorage.removeItem(RELOAD_STORAGE_KEY)
      applyFavicon(`${process.env.PUBLIC_URL}/favicon.ico`)
      return
    }

    try {
      const cached = localStorage.getItem(STORAGE_KEY)
      if (cached) {
        const parsed = JSON.parse(cached)
        const account = getCachedAccount(parsed)
        if (isAccountForTrackingHost(account, hostname, parsed.hostname)) {
          setLogoUrl(account.logo_url || null)
          setFavIconUrl(account.fav_icon_url || null)
          setAccountName(account.account_name || null)
          setSaasAccountId(account.id || null)
          setBrandingFailed(false)
          sessionStorage.removeItem(RELOAD_STORAGE_KEY)
          applyFavicon(account.fav_icon_url)
          setIsBrandingLoading(false)
          setBrandingResolved(true)
          return
        }
      }
    } catch (_) {}

    setIsBrandingLoading(true)
    fetchSaasAccountByDomain(hostname)
      .then((res) => {
        const isSuccess = res?.status === 200 && res?.data?.data && Object.keys(res.data.data).length > 0
        if (!isSuccess) {
          handleBrandingFailure()
          return
        }

        const account = res.data.data
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ hostname, account }))
        } catch (_) {}
        setLogoUrl(account.logo_url || null)
        setFavIconUrl(account.fav_icon_url || null)
        setAccountName(account.account_name || null)
        setSaasAccountId(account.id || null)
        setBrandingFailed(false)
        sessionStorage.removeItem(RELOAD_STORAGE_KEY)
        applyFavicon(account.fav_icon_url)
      })
      .catch((err) => {
        console.error('Failed to fetch saas account branding:', err)
        handleBrandingFailure()
      })
      .finally(() => {
        setIsBrandingLoading(false)
        setBrandingResolved(true)
      })
  }, [isOwner, hostname])

  return {
    saasAccountId,
    logoUrl,
    favIconUrl,
    accountName,
    isOwner,
    isBrandingLoading,
    brandingResolved,
    brandingFailed,
    canUseTracking: isOwner || (!isBrandingLoading && brandingResolved && !brandingFailed && !!saasAccountId),
  }
}

export default useSaasAccount
